import { QueryClient } from "@tanstack/react-query";

/**
 * Re-exported so an app never declares `@tanstack/react-query` itself.
 *
 * `DECISION ADDED A35` puts the Query client here precisely so three apps
 * cannot diverge on it, and an app with its own dependency line can pin its own
 * version , at which point two `QueryClient` classes exist, a provider from one
 * does not satisfy a hook from the other, and the symptom is "no QueryClient
 * set" in an app whose provider is plainly right there.
 */
export type { QueryClient } from "@tanstack/react-query";

/**
 * `P0-UIK-013`: the TanStack Query client and its auth wiring.
 *
 * **Danger sentence.** The default retry policy is the wrong policy for this
 * API, and wrongly in the direction that hurts. 4.5.5 gives every refusal a
 * status and a French sentence written beside the guard that produced it, and
 * 6.1.8's four layers answer `401`, `403`, `404`, `409` and `410` , every one
 * of which is **an answer**, not a failure. Retrying an answer three times
 * makes the caller wait three round trips to be told the same thing, and on a
 * 3G handset in Bamako (4.6.1's stated device) that is the difference between
 * a screen and a spinner. Worse, a retried `409 stage_guard_failed` on a
 * mutation can be a retried ATTEMPT to advance a dossier.
 *
 * So: a 4xx is never retried. A 5xx and a transport failure are, because those
 * are the failures a retry can actually fix.
 *
 * **Where the refresh is NOT.** 4.5.11 gives the token refresh to the generated
 * client: it *"injects the access token, refreshes it on `401` once"*. This
 * file must not do it a second time , two independent refreshers race, and the
 * loser writes back a token the winner already rotated, which signs the user
 * out at random. What belongs here is the answer to the `401` that SURVIVED
 * that refresh: the session is genuinely over, and exactly one thing should
 * happen, once, no matter how many queries were in flight.
 */

/** The shape the shell reads off a rejection. Structural on purpose: 4.5.11's `ApiError` arrives with `P0-INF-026`, and the shell needs only the status until it does. */
export interface HttpFailure {
  readonly status: number;
}

/** The HTTP status of a rejection, or `null` when it is not an HTTP answer at all. */
export function statusOf(failure: unknown): number | null {
  if (typeof failure !== "object" || failure === null) return null;
  const status = (failure as Partial<HttpFailure>).status;
  return typeof status === "number" ? status : null;
}

export const MAX_RETRIES = 2;

/**
 * `false` for anything the server MEANT, `true` for what it merely suffered.
 *
 * Exported and named so it can be asserted directly: a retry policy expressed
 * only as an inline closure inside `new QueryClient` is a policy nothing can
 * test without booting a client and provoking real failures.
 */
export function shouldRetry(attempt: number, failure: unknown): boolean {
  const status = statusOf(failure);
  // A rejection with no status is a transport failure , offline, DNS, a
  // dropped connection , which is the case retrying exists for.
  if (status !== null && status >= 400 && status < 500) return false;
  return attempt < MAX_RETRIES;
}

export interface QueryClientOptions {
  /**
   * Called ONCE when a `401` reaches the shell, meaning 4.5.11's single refresh
   * already failed. The app clears its session and shows the login; the shell
   * does not navigate, because it owns no screens.
   */
  readonly onSessionExpired: () => void;
}

export function createQueryClient(options: QueryClientOptions): QueryClient {
  let expired = false;

  /**
   * Fired at most once per client. Ten queries in flight when a session ends
   * produce ten `401`s, and ten sign-outs would be ten redirects , the user
   * sees the login screen flicker and any half-typed credential is lost.
   */
  const expireOnce = (failure: unknown): void => {
    if (statusOf(failure) !== 401 || expired) return;
    expired = true;
    options.onSessionExpired();
  };

  const client = new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        // 4.6.1: the client app runs on 3G. Refetching on every window focus
        // spends a user's data to re-answer a question already on screen.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: {
        // A mutation is never retried automatically, whatever the status. The
        // API is idempotent only where an `Idempotency-Key` is sent (4.5.11),
        // and a shared default cannot know which call that is. A resend the
        // caller did not ask for is a second attempt to change something.
        retry: false,
      },
    },
  });

  client.getQueryCache().config.onError = expireOnce;
  client.getMutationCache().config.onError = expireOnce;

  return client;
}
